import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { FiMessageCircle, FiX, FiSend, FiImage } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './AiAssistantFab.css';

const INTRO =
  'Je praat met één tool op de server: die kiest zelf welke dataset te laden (action + parameters). ' +
  'Voorbeelden: globaal overzicht, product zoeken, verkoop per kanaal, week vs. vorig jaar, traaglopers met voorraad. ' +
  'Je kunt ook foto\'s bijvoegen. Vraag bijv. omzet_weer_prognose voor verwachte omzet met weer.';

const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function readFileAsImageAttachment(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      const data = comma >= 0 ? result.slice(comma + 1) : '';
      resolve({
        media_type: file.type,
        data,
        preview: result,
        name: file.name,
      });
    };
    reader.onerror = () => reject(new Error('Bestand kon niet worden gelezen'));
    reader.readAsDataURL(file);
  });
}

function toApiMessage(m) {
  const base = { role: m.role, content: m.content };
  if (m.role === 'user' && Array.isArray(m.images) && m.images.length > 0) {
    base.images = m.images.map(({ media_type, data }) => ({ media_type, data }));
  }
  return base;
}

export default function AiAssistantFab() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(() => [
    { role: 'system', content: INTRO },
  ]);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages, sending, pendingImages]);

  const onPickImages = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;
      setAttachError('');

      const slotsLeft = MAX_IMAGES - pendingImages.length;
      if (slotsLeft <= 0) {
        setAttachError(`Maximaal ${MAX_IMAGES} afbeeldingen per bericht.`);
        return;
      }

      const next = [...pendingImages];
      for (const file of files.slice(0, slotsLeft)) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          setAttachError('Alleen JPG, PNG, GIF of WebP.');
          continue;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          setAttachError('Afbeelding mag maximaal 4 MB zijn.');
          continue;
        }
        try {
          const att = await readFileAsImageAttachment(file);
          next.push({ ...att, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
        } catch (err) {
          setAttachError(err.message || 'Upload mislukt');
        }
      }
      setPendingImages(next.slice(0, MAX_IMAGES));
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [pendingImages]
  );

  const removePendingImage = useCallback((id) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== id));
    setAttachError('');
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    const hasImages = pendingImages.length > 0;
    if ((!text && !hasImages) || sending) return;

    const content = text || '(Zie bijgevoegde afbeelding(en).)';
    const imagesForMsg = pendingImages.map(({ media_type, data, preview, name, id }) => ({
      media_type,
      data,
      preview,
      name,
      id,
    }));

    const userMsg = { role: 'user', content, images: imagesForMsg };
    const historyForApi = [...messages, userMsg]
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map(toApiMessage);

    setInput('');
    setPendingImages([]);
    setAttachError('');
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      const res = await api.post('/ai-assistant/chat', {
        messages: historyForApi,
        pagePath: location.pathname + (location.search || ''),
      });
      const reply = String(res.data?.reply || '').trim() || '(Leeg antwoord)';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Onbekende fout';
      setMessages((prev) => [...prev, { role: 'error', content: msg }]);
    } finally {
      setSending(false);
    }
  }, [input, pendingImages, sending, messages, location.pathname, location.search]);

  const clearChat = useCallback(() => {
    setMessages([{ role: 'system', content: INTRO }]);
    setPendingImages([]);
    setAttachError('');
    setInput('');
  }, []);

  if (loading || !user) {
    return null;
  }

  const canSend = !sending && (input.trim().length > 0 || pendingImages.length > 0);

  const panel = open ? (
    <>
      <div
        className="ai-assistant-backdrop"
        aria-hidden
        onClick={() => setOpen(false)}
      />
      <div className="ai-assistant-panel" role="dialog" aria-labelledby="ai-assistant-title">
        <div className="ai-assistant-panel-header">
          <div>
            <h2 id="ai-assistant-title" className="ai-assistant-panel-title">
              Inkoop-assistent
            </h2>
            <p className="ai-assistant-panel-sub">Haalt data op wanneer nodig (alleen-lezen, per vraag)</p>
          </div>
          <button
            type="button"
            className="ai-assistant-icon-btn"
            onClick={() => setOpen(false)}
            aria-label="Paneel sluiten"
          >
            <FiX size={22} />
          </button>
        </div>
        <div className="ai-assistant-messages" ref={listRef}>
          {messages.map((m, i) => (
            <div
              key={i}
              className={
                m.role === 'user'
                  ? 'ai-assistant-msg ai-assistant-msg--user'
                  : m.role === 'error'
                    ? 'ai-assistant-msg ai-assistant-msg--error'
                    : m.role === 'system'
                      ? 'ai-assistant-msg ai-assistant-msg--system'
                      : 'ai-assistant-msg ai-assistant-msg--assistant'
              }
            >
              {Array.isArray(m.images) && m.images.length > 0 ? (
                <div className="ai-assistant-msg-images">
                  {m.images.map((img) => (
                    <img
                      key={img.id || img.preview}
                      src={img.preview || `data:${img.media_type};base64,${img.data}`}
                      alt={img.name || 'Bijlage'}
                      className="ai-assistant-msg-image"
                    />
                  ))}
                </div>
              ) : null}
              {m.content}
            </div>
          ))}
          {sending ? <div className="ai-assistant-typing">Bezig met antwoord…</div> : null}
        </div>
        <div className="ai-assistant-footer">
          <div className="ai-assistant-toolbar">
            <button type="button" className="ai-assistant-clear" onClick={clearChat}>
              Wis gesprek
            </button>
          </div>
          {pendingImages.length > 0 ? (
            <div className="ai-assistant-pending-images">
              {pendingImages.map((img) => (
                <div key={img.id} className="ai-assistant-pending-image">
                  <img src={img.preview} alt={img.name || 'Voorbeeld'} />
                  <button
                    type="button"
                    className="ai-assistant-pending-remove"
                    onClick={() => removePendingImage(img.id)}
                    aria-label="Afbeelding verwijderen"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {attachError ? <p className="ai-assistant-attach-error">{attachError}</p> : null}
          <form
            className="ai-assistant-form"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              multiple
              className="ai-assistant-file-input"
              onChange={(e) => onPickImages(e.target.files)}
            />
            <button
              type="button"
              className="ai-assistant-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || pendingImages.length >= MAX_IMAGES}
              title={`Foto toevoegen (max ${MAX_IMAGES})`}
              aria-label="Foto toevoegen"
            >
              <FiImage size={18} aria-hidden />
            </button>
            <textarea
              className="ai-assistant-input"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stel je vraag of voeg een foto toe…"
              disabled={sending}
              aria-label="Vraag voor de assistent"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button type="submit" className="ai-assistant-send" disabled={!canSend}>
              <FiSend size={18} aria-hidden />
            </button>
          </form>
        </div>
      </div>
    </>
  ) : null;

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="ai-assistant-fab"
          onClick={() => setOpen(true)}
          aria-label="Inkoop-assistent openen"
          title="Inkoop-assistent"
        >
          <FiMessageCircle aria-hidden />
        </button>
      ) : null}
      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
